import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ModalProvider } from "@/contexts/ModalContext";
import { lazy, Suspense } from "react";
import { MotionConfig } from "framer-motion";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFoundPage from "./pages/NotFoundPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookieBanner from "@/components/CookieBanner";
import IntegrationHub from "./pages/docs/IntegrationHub";
import OgImagePreview from "@/pages/OgImagePreview";
import Blog, { BlogPost } from "@/pages/Blog";

const HubLayout = lazy(() => import("@/components/hub/HubLayout"));
const PartnerAdmin = lazy(() => import("@/pages/hub/admin/PartnerAdmin"));
const PaymentSuccess = lazy(() => import("@/pages/hub/PaymentSuccess"));
const TechnicalBrief = lazy(() => import("@/pages/TechnicalBrief"));
const ExecutiveSummary = lazy(() => import("@/pages/ExecutiveSummary"));
const Simulator = lazy(() => import("@/pages/Simulator"));
const NeutralityPromisePage = lazy(() => import("@/pages/NeutralityPromisePage"));
const WhatIsPage = lazy(() => import("@/pages/WhatIsPage"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const NeuroAgentDesk = lazy(() => import("@/pages/hub/admin/NeuroAgentDesk"));
const UaTestDashboard = lazy(() => import("@/pages/hub/admin/UaTestDashboard"));
const MarketingReports = lazy(() => import("@/pages/hub/admin/MarketingReports"));
const OnboardingAudit = lazy(() => import("@/pages/hub/admin/OnboardingAudit"));
const PitchDeck = lazy(() => import("@/pages/hub/admin/PitchDeck"));
const Crm = lazy(() => import("@/pages/hub/admin/Crm"));
const DocumentValuesEditor = lazy(() => import("@/pages/hub/admin/DocumentValuesEditor"));
const DataGovernance = lazy(() => import("@/pages/hub/DataGovernance"));
const PartnerDashboard = lazy(() => import("@/pages/hub/PartnerDashboard"));
const OperationalStatus = lazy(() => import("@/pages/hub/OperationalStatus"));
const ReputationAccess = lazy(() => import("@/pages/hub/ReputationAccess"));
const LongitudinalData = lazy(() => import("@/pages/hub/LongitudinalData"));
const GuestPortalSetup = lazy(() => import("@/pages/hub/GuestPortalSetup"));
const RelationshipGraph = lazy(() => import("@/pages/hub/RelationshipGraph"));
const PredictiveSignals = lazy(() => import("@/pages/hub/PredictiveSignals"));
const DataPipeline = lazy(() => import("@/pages/hub/DataPipeline"));
const Sandbox = lazy(() => import("@/pages/hub/Sandbox"));
const GoLiveChecklist = lazy(() => import("@/pages/hub/GoLiveChecklist"));
const Billing = lazy(() => import("@/pages/hub/Billing"));
const DeveloperSettings = lazy(() => import("@/pages/hub/DeveloperSettings"));
const Developers = lazy(() => import("@/pages/hub/Developers"));
const WebhookLogs = lazy(() => import("@/pages/hub/WebhookLogs"));
const SystemStatus = lazy(() => import("@/pages/hub/SystemStatus"));
const AccountSettings = lazy(() => import("@/pages/hub/AccountSettings"));
import { HelmetProvider } from "react-helmet-async";
import { OrganizationJsonLd } from "@/components/JsonLd";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <MotionConfig reducedMotion="user">
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ModalProvider>
            <OrganizationJsonLd />
            <Suspense fallback={<div />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/docs/integrate" element={<IntegrationHub />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/technical-brief" element={<TechnicalBrief />} />
              <Route path="/executive-summary" element={<ExecutiveSummary />} />
              <Route path="/simulator" element={<Simulator />} />
              <Route path="/neutrality-promise" element={<NeutralityPromisePage />} />
              <Route path="/what-is" element={<WhatIsPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/og-preview" element={<OgImagePreview />} />
              
              {/* Partner Hub Routes */}
              <Route path="/hub" element={<HubLayout />}>
                <Route index element={<PartnerDashboard />} />
              <Route path="dashboard" element={<PartnerDashboard />} />
              <Route path="data-governance" element={<DataGovernance />} />
              <Route path="operational-status" element={<OperationalStatus />} />

              {/* New consolidated Developer Portal — default tab is #guide (handled in component) */}
              <Route path="developers" element={<Developers />} />

              {/* Legacy redirects → new Developer Portal tabs */}
              {/* DEPRECATED — retire these after confirming no external links remain */}
              <Route path="quickstart" element={<Navigate to="/hub/developers#guide" replace />} />
              <Route path="api"        element={<Navigate to="/hub/developers#api" replace />} />
              <Route path="signals"    element={<Navigate to="/hub/developers#signals" replace />} />

              <Route path="reputation" element={<ReputationAccess />} />
              <Route path="longitudinal" element={<LongitudinalData />} />
              <Route path="guest-portal" element={<GuestPortalSetup />} />
              <Route path="relationship" element={<RelationshipGraph />} />
              <Route path="predictive" element={<PredictiveSignals />} />
              <Route path="pipeline" element={<DataPipeline />} />
              <Route path="sandbox" element={<Sandbox />} />
              <Route path="checklist" element={<GoLiveChecklist />} />
              <Route path="billing" element={<Billing />} />
              <Route path="developer-settings" element={<DeveloperSettings />} />
              <Route path="webhooks" element={<WebhookLogs />} />
              <Route path="status" element={<SystemStatus />} />
              <Route path="account-settings" element={<AccountSettings />} />
              <Route path="admin" element={<PartnerAdmin />} />
              <Route path="admin/neuro-agent" element={<NeuroAgentDesk />} />
              <Route path="admin/ua-test" element={<UaTestDashboard />} />
              <Route path="admin/marketing" element={<MarketingReports />} />
              <Route path="admin/onboarding-audit" element={<OnboardingAudit />} />
              <Route path="admin/pitch-deck" element={<PitchDeck />} />
              <Route path="admin/crm" element={<Crm />} />
              <Route path="admin/document-values" element={<DocumentValuesEditor />} />
              <Route path="payment-success" element={<PaymentSuccess />} />
              </Route>
              
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/not-found" element={<NotFoundPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<Navigate to="/not-found" replace />} />
            </Routes>
            </Suspense>
            <CookieBanner />
            </ModalProvider>
          </BrowserRouter>
          </MotionConfig>
        </TooltipProvider>
      </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
